import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useOutletContext } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AdminPage } from "./components/AdminPage";
import { AdminHomeTab } from "./components/admin/AdminHomeTab";
import { AdminTimelineTab } from "./components/admin/AdminTimelineTab";
import { AdminHobbiesTab } from "./components/admin/AdminHobbiesTab";
import { AdminCausesTab } from "./components/admin/AdminCausesTab";
import { AdminBlogTab } from "./components/admin/AdminBlogTab";
import { AdminSocialsTab } from "./components/admin/AdminSocialsTab";
import { AdminDashboardTab } from "./components/admin/AdminDashboardTab";
import { AdminProfileTab } from "./components/admin/AdminProfileTab";
import { AdminProjectsTab } from "./components/admin/AdminProjectsTab";
import { BlogEditorPage } from "./components/admin/editor/BlogEditorPage";
import { AdminAnalyticsTab } from "./components/admin/AdminAnalyticsTab";
import { AdminFaqsTab } from "./components/admin/AdminFaqsTab";
import { AdminTestimonialsTab } from "./components/admin/AdminTestimonialsTab";
import { AdminMediaTab } from "./components/admin/AdminMediaTab";
import "./index.css";

interface OutletContextType {
  content: any;
  fetchContent: () => void;
}

function AdminDashboardRoute() {
  return <AdminDashboardTab />;
}

function AdminAnalyticsRoute() {
  return <AdminAnalyticsTab />;
}

function AdminHomeRoute() {
  const { content, fetchContent } = useOutletContext<OutletContextType>();
  return <AdminHomeTab initialSettings={content.settings || []} timeline={content.timeline || []} onRefresh={fetchContent} />;
}

function AdminProfessionalRoute() {
  const { content, fetchContent } = useOutletContext<OutletContextType>();
  return <AdminTimelineTab type="professional" events={content.timeline || []} onRefresh={fetchContent} />;
}

function AdminAcademicRoute() {
  const { content, fetchContent } = useOutletContext<OutletContextType>();
  return <AdminTimelineTab type="academic" events={content.timeline || []} onRefresh={fetchContent} />;
}

function AdminHobbyRoute() {
  const { content, fetchContent } = useOutletContext<OutletContextType>();
  return <AdminHobbiesTab hobbies={content.hobbies || []} onRefresh={fetchContent} />;
}

function AdminProjectsRoute() {
  const { content, fetchContent } = useOutletContext<OutletContextType>();
  return <AdminProjectsTab projects={content.projects || []} onRefresh={fetchContent} />;
}

function AdminCommunityRoute() {
  const { content, fetchContent } = useOutletContext<OutletContextType>();
  return (
    <div className="space-y-8">
      <AdminTimelineTab type="community" events={content.timeline || []} onRefresh={fetchContent} />
      <div className="border-t border-border/40 pt-6">
        <AdminCausesTab causes={content.causes || []} onRefresh={fetchContent} />
      </div>
    </div>
  );
}

function AdminBlogRoute() {
  const { content, fetchContent } = useOutletContext<OutletContextType>();
  return (
    <AdminBlogTab
      blogs={content.blogs || []}
      categories={content.categories || []}
      tags={content.tags || []}
      onRefresh={fetchContent}
    />
  );
}

function AdminBlogEditorRoute() {
  const { content, fetchContent } = useOutletContext<OutletContextType>();
  return (
    <BlogEditorPage
      blogs={content.blogs || []}
      categories={content.categories || []}
      tags={content.tags || []}
      onRefresh={fetchContent}
    />
  );
}

function AdminContactRoute() {
  const { content, fetchContent } = useOutletContext<OutletContextType>();
  return <AdminSocialsTab socials={content.socials || []} onRefresh={fetchContent} />;
}

function AdminProfileRoute() {
  return <AdminProfileTab />;
}

function AdminFaqRoute() {
  const { content, fetchContent } = useOutletContext<OutletContextType>();
  return <AdminFaqsTab faqs={content.faqs || []} onRefresh={fetchContent} />;
}

function AdminTestimonialsRoute() {
  const { content, fetchContent } = useOutletContext<OutletContextType>();
  return <AdminTestimonialsTab testimonials={content.testimonials || []} onRefresh={fetchContent} />;
}

function AdminMediaRoute() {
  return <AdminMediaTab />;
}

function Layout() {
  return (
    <div className="min-h-screen w-full flex flex-col bg-background text-foreground transition-colors duration-300">
      <header className="w-full border-b border-border/80 bg-card/40 py-4 px-4 md:px-8 flex items-center justify-between relative z-10">
        <div className="max-w-6xl mx-auto w-full flex items-center justify-between">
          <span className="font-bold text-brand-primary">Uğurcan Uzunkaya | Admin Console</span>
        </div>
      </header>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 md:px-8 py-8 relative z-10">
        <Routes>
          <Route path="/" element={<AdminPage />}>
            <Route index element={<AdminDashboardRoute />} />
            <Route path="analytics" element={<AdminAnalyticsRoute />} />
            <Route path="home" element={<AdminHomeRoute />} />
            <Route path="professional" element={<AdminProfessionalRoute />} />
            <Route path="academic" element={<AdminAcademicRoute />} />
            <Route path="hobby" element={<AdminHobbyRoute />} />
            <Route path="projects" element={<AdminProjectsRoute />} />
            <Route path="community" element={<AdminCommunityRoute />} />
            <Route path="blog" element={<AdminBlogRoute />} />
            <Route path="blog/editor/:slug?" element={<AdminBlogEditorRoute />} />
            <Route path="contact" element={<AdminContactRoute />} />
            <Route path="profile" element={<AdminProfileRoute />} />
            <Route path="faq" element={<AdminFaqRoute />} />
            <Route path="testimonials" element={<AdminTestimonialsRoute />} />
            <Route path="media" element={<AdminMediaRoute />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <footer className="w-full border-t border-border/80 bg-card/40 py-6 px-4 md:px-8 text-center text-xs text-muted-foreground relative z-10">
        <p>© 2026 Uğurcan Uzunkaya. All rights reserved.</p>
      </footer>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export function App() {
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <Layout />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
